import { BillItemState, RootState } from '@/types/store';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import {
  BillItemFields,
  BillItemInput,
  QuoteTemplateLoadInput,
  quoteTemplateSearch,
  QuoteTemplateInput
} from '@/types/graphql/schemaTypes';
import { billItemService } from '@/services/BillItemService';
import { ApolloQueryResult } from 'apollo-client';
import i18n from '@/plugins/i18n';

// initial state
const state: BillItemState = {
  upsertBillItemAttrs: null,
  billItemModalBaseProps: null,
  billItemToDelete: null,
  quoteTemplates: [],
  closeQuoteDeleteTemplate: false
};

const getters: GetterTree<BillItemState, RootState> = {
  searchTemplates(state: BillItemState) {
    return (search: string) => state.quoteTemplates.filter((item) => {
      return item.name.toLowerCase().includes(search.toLocaleLowerCase());
    });
  }
};

// actions
const actions: ActionTree<BillItemState, RootState> = {
  async fetchTemplates({ commit }): Promise<void> {
    billItemService.fetchTemplates()
      .then((response: ApolloQueryResult<quoteTemplateSearch>): void => {
        commit('setTemplates', response.data.quoteTemplates);
      });
  },

  async loadTemplate({ commit, dispatch }, loadArgs: QuoteTemplateLoadInput): Promise<void> {
    await billItemService.loadTemplate(loadArgs).then(() => {
      // refresh inquiry
      // FIXME: de-comment me when Quote.vue use store to fetch data
      // dispatch('Inquiry/fetchInquiry', { inquiryId: loadArgs.shipmentId }, { root: true });
    }).catch((error) => {
      commit('Alert/addAlert', {
        type: 'error',
        message: error.message
      }, { root: true });
    });
  },

  async createTemplate({ commit, dispatch }, quoteTemplateArgs: QuoteTemplateInput): Promise<void> {
    billItemService.createTemplate(quoteTemplateArgs).then(() => {
      dispatch('fetchTemplates');
      commit('Alert/addAlert', {
        type: 'success',
        message: i18n.t('success.created'),
        time: 5000
      }, { root: true });
    });
  },

  async deleteTemplate({ commit, dispatch }, deleteArgs): Promise<void> {
    await billItemService.deleteTemplate(deleteArgs).then(() => {
      commit('setCloseQuoteTemplate', true);
      dispatch('fetchTemplates');
      commit(
        'Alert/addAlert', {
          type: 'success',
          message: i18n.t('success.deleted'),
          time: 5000
        },
        { root: true }
      );
    }).catch((error) => {
      commit(
        'Alert/addAlert', {
          type: 'error',
          message: error.message
        },
        { root: true }
      );
    });
  },

  async upsertBillItems({ commit, dispatch }, upsertArgs) {
    await billItemService.upsertBillItems(upsertArgs.billItemArgs, upsertArgs.shipmentId)
      .then(() => {
        commit(
          'Alert/addAlert', {
            type: 'success',
            message: i18n.t('success.updated'),
            time: 5000
          },
          { root: true }
        );
      }).catch(() => {
        commit(
          'Alert/addAlert', {
            type: 'error',
            message: i18n.t('error.push')
          },
          { root: true }
        );
      });
  }
};

// mutations
const mutations: MutationTree<BillItemState> = {
  setTemplates(billItemState, quoteTemplates: any): void {
    billItemState.quoteTemplates = quoteTemplates;
  },
  setCloseQuoteTemplate(billItemState, close: boolean): void {
    billItemState.closeQuoteDeleteTemplate = close;
  }
};

export const billItem: Module<BillItemState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
